import { useDisclosure } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  AccountStatusEnum,
  ContactStatusEnum,
  ContractStatusEnum,
  CrmOpportunityForInput,
  HubspotAssociatedCompany,
  IAccountRespSchema,
  IAccountSchema,
  IBulkContactResponseSchema,
  ICustomFieldRecordSchema,
  ILegalEntityResponseSchema,
  IQuoteRequestInputSchemaExtended,
  IQuoteRequestSchema,
  IQuoteRequestSchemaExtended,
  IQuoteRespSchema,
  QuoteRequestSchemaExtended,
} from '@monetize/types/app';
import { DEFAULT_PAGER, Maybe } from '@monetize/types/common';
import { useQueryClient } from '@tanstack/react-query';
import { formatISO } from 'date-fns/formatISO';
import isEmpty from 'lodash/isEmpty';
import { FC, useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { getAccountById, useCreateAccount } from '../../../api/accountsService';
import { handleApiErrorToast } from '../../../api/axios';
import {
  doGetAccountContacts,
  useUpsertHubspotContactsInMonetizeNow,
} from '../../../api/contactsService';
import {
  cpqServiceQueryKeys,
  doGetBlueprintQuotes,
  doGetContractsByAccount,
  useCreateQuoteFromBlueprint,
  useGetOrCreateOpportunityWithCrmLink,
} from '../../../api/cpqService';
import {
  doAssociateHubspotCompanyWithAccount,
  doAssociateHubspotContactWithContact,
} from '../../../api/crmService';
import { useActiveGuidedQuotingProcess } from '../../../api/guidedQuotingService';
import { useGetListData } from '../../../api/queryUtils';
import {
  ILegalEntitiesSelector,
  selectLegalEntities,
} from '../../../api/selectors';
import { MPageLoader } from '../../../components/Monetize';
import { ROUTES } from '../../../constants';
import { INITIAL_FORM_VALUES } from '../../../constants/quotes';
import { getQuoteEditRoute } from '../../../constants/routes';
import { useNetTerms, useQuote } from '../../../hooks';
import { useBackNavigate } from '../../../hooks/useBackNavigate';
import { useCurrencies } from '../../../hooks/useCurrencies';
import { useIsTenantEsignConfiguredWithLoading } from '../../../hooks/useIsTenantEsignConfigured';
import { useDocumentHead } from '../../../services/documentHead';
import { logger } from '../../../services/logger';
import Sentry from '../../../services/sentry';
import { appGlobalDataState } from '../../../store/global.store';
import { NewGuidedQuoteModal } from './components/guidedQuote/NewGuidedQuoteModal';
import {
  NewQuoteForm,
  NewQuoteFormDataTypes,
} from './components/NewQuoteForm/NewQuoteForm';
import {
  getDefaultEsignIds,
  getQuoteDescription,
  parseCrmQuoteFieldMapping,
} from './quoteUtils';

const useNewQuoteFormData = (): NewQuoteFormDataTypes => {
  const { open: isOpen, onOpen, onClose } = useDisclosure();
  const [loading, setLoading] = useState<boolean>(false);
  const [accountById, setAccountById] = useState<IAccountRespSchema | null>(
    null,
  );

  const fetchAccountById = async (
    accountId: string,
  ): Promise<IAccountRespSchema | null> => {
    setLoading(true);
    try {
      const res = await getAccountById(accountId);

      setAccountById(res);
      return res;
    } catch (error) {
      handleApiErrorToast(error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    isOpen,
    loading,
    accountById,
    onOpen,
    onClose,
    fetchAccountById,
  };
};

export const NewQuoteFormPage: FC = () => {
  const { accountId: accountIdParam = '' } = useParams();
  const [searchParams] = useSearchParams();
  const { navigate, navigateBack } = useBackNavigate(1);
  const { setDocTitle } = useDocumentHead();
  const queryClient = useQueryClient();
  const { accountById, fetchAccountById, isOpen, onOpen } =
    useNewQuoteFormData();
  const { open: isGuidedSellingOpen, onOpen: onGuidedSellingOpen } =
    useDisclosure();
  const { mutateAsync: doGetOrCreateOpportunity } =
    useGetOrCreateOpportunityWithCrmLink();
  const { mutateAsync: createQuoteFromBlueprint } =
    useCreateQuoteFromBlueprint();
  const { createQuote, updateQuoteContacts } = useQuote();
  const { defaultNetTerm } = useNetTerms();
  const [account, setAccount] = useState<IAccountRespSchema | null>(null);
  const [accountName, setAccountName] = useState<string>('');
  const { isLoading: isLoadingEsignConfig, isTenantEsignConfigured } =
    useIsTenantEsignConfiguredWithLoading();
  const { currencies } = useCurrencies();

  const { isLoading: isGuidedQuotingLoading, data: guidedQuotingProcess } =
    useActiveGuidedQuotingProcess();

  /** These parameters are set from Quotes created via CRM */
  const hasSkipModalSearchParam = searchParams.has('skipModal');
  const opportunityIdSearchParam = searchParams.get('opportunityId') as string;
  const contactIdSearchParam = searchParams.get('contactId') as string;
  const quoteFieldMapping = searchParams.get('fieldMapping') as string;

  const parsedFieldMapping = parseCrmQuoteFieldMapping(quoteFieldMapping);
  const additionalFields: {
    customId?: string;
    description?: string;
    customFields?: Record<string, any>;
    linkedAccountId?: string;
  } = {};

  if (!isEmpty(parsedFieldMapping)) {
    additionalFields.customId = parsedFieldMapping.customId || null;
    additionalFields.description = parsedFieldMapping.description || null;
    additionalFields.customFields = parsedFieldMapping.customFields || {};
    additionalFields.linkedAccountId =
      parsedFieldMapping.linkedAccountId || null;
  }

  const { data: legalEntitiesData } = useGetListData<
    ILegalEntityResponseSchema,
    ILegalEntitiesSelector
  >(
    'legalEntities',
    {
      config: { ...DEFAULT_PAGER, rows: 50 },
    },
    {
      select: selectLegalEntities,
      refetchOnWindowFocus: false,
    },
  );

  const { setValue, getValues, ...formMethods } = useForm<
    IQuoteRequestInputSchemaExtended,
    unknown,
    IQuoteRequestSchemaExtended
  >({
    mode: 'onChange',
    resolver: zodResolver(QuoteRequestSchemaExtended),
    defaultValues: {
      contractStartDate: formatISO(new Date(), { representation: 'date' }),
      ...INITIAL_FORM_VALUES,
      legalEntityId: parsedFieldMapping.legalEntityId,
    },
  });

  useEffect(() => {
    if (defaultNetTerm) {
      setValue('netTerms', defaultNetTerm.value);
    }
  }, [defaultNetTerm, setValue]);

  useEffect(() => {
    setDocTitle('New Quote');
  }, [setDocTitle]);

  useEffect(() => {
    !isLoadingEsignConfig && !isGuidedQuotingLoading && initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingEsignConfig, isGuidedQuotingLoading]);

  const { mutateAsync: doCreateAccount } = useCreateAccount();
  const { mutateAsync: doUpsertHubspotContacts } =
    useUpsertHubspotContactsInMonetizeNow();
  const { hasHubspotConfigured } = useAtomValue(appGlobalDataState);
  /**
   * Initialized everything needed for the form
   * Optionally skip opening the modal based on query parameters
   * Paths:
   * * User creating quote with no account
   * * User creating quote from an existing account
   * * User creating quote from CRM with existing account and opportunity, no contract
   *   * In this case skipModal=true and the modal will not be shown
   *   * If there are more than 1 currency or more than 1 legal entity, then the modal will be shown
   * * User creating quote from CRM with existing account and opportunity, existing contracts
   *   * In this case skipModal=true, but the modal is shown to the user to select the correct action NEW/AMEND/RENEW and select the correct contract
   */
  async function initialize() {
    try {
      let skipModal = hasSkipModalSearchParam;
      if (opportunityIdSearchParam) {
        setValue('opportunityId', opportunityIdSearchParam);
      }

      if (accountIdParam) {
        await fetchAndSetAccount();
      }

      // If there are active contracts, then don't skip the modal
      if (skipModal && accountIdParam) {
        const contracts = await doGetContractsByAccount(
          accountIdParam,
          { rows: 1 },
          {
            status: {
              in: [
                ContractStatusEnum.ACTIVE,
                ContractStatusEnum.PENDING,
                ContractStatusEnum.FINISHED,
              ],
            },
          },
        );
        skipModal = contracts.totalElements === 0;
      }

      // If we have blueprints, then don't skip the modal
      if (skipModal) {
        const bluePrintResponse = await doGetBlueprintQuotes(
          {
            ...DEFAULT_PAGER,
            rows: 1,
          },
          { isActive: 'true' },
        );
        skipModal = bluePrintResponse.totalElements === 0;
      }

      // let user opt-in to guided quoting if they want
      if (skipModal && guidedQuotingProcess) {
        skipModal = false;
      }

      // If there are multiple currencies or legal entities, then don't skip the modal
      if (skipModal) {
        if (
          currencies.length > 1 ||
          (legalEntitiesData?.legalEntities?.length &&
            legalEntitiesData?.legalEntities?.length > 1)
        ) {
          skipModal = false;
        }
      }

      // If HubSpot is connected, show the modal so the user can select a linked
      // account from the deal's associated companies
      if (skipModal && hasHubspotConfigured) {
        skipModal = false;
      }

      if (skipModal) {
        handleCreateQuote();
      } else {
        onOpen();
      }
    } catch (ex) {
      handleApiErrorToast(ex);
      if (hasSkipModalSearchParam) {
        navigate(ROUTES.SALES_LANDING, { replace: true });
      }
    }
  }

  // Get account and update form values
  async function fetchAndSetAccount() {
    const account = await fetchAccountById(accountIdParam);
    if (account) {
      setAccount(account as unknown as IAccountRespSchema);
      setAccountName(account?.accountName || '');
      setValue('accountId', accountIdParam);
      setValue('description', getQuoteDescription(account?.accountName));
      setValue(
        'legalEntityId',
        parsedFieldMapping.legalEntityId ||
          account.defaultLegalEntityId ||
          legalEntitiesData?.defaultLegalEntity?.id ||
          '',
      );
    }

    // A CRM-mapped currency wins over the account default, otherwise the modal
    // shows one currency while the create payload is sent with another.
    const currency = parsedFieldMapping.currency || account?.defaultCurrency;
    if (currency) {
      setValue('currency', currency);
    }
    return account;
  }

  function handleCancel() {
    navigateBack();
  }

  /**
   * For guided selling, update the payload to apply any CRM mapped fields
   */
  const getGuidedSellingInitialQuoteFieldValues = () => {
    const crmFieldMapping = structuredClone(parsedFieldMapping || {});
    delete crmFieldMapping['contacts.esign'];

    return crmFieldMapping as Partial<IQuoteRequestSchema>;
  };

  /**
   * Create a new quote and navigate to the edit page
   *
   */
  const handleCreateQuote = async ({
    crmOpportunity,
    customFields,
    useGuidedSelling = false,
    selectedBlueprintId,
    hubspotLinkedCompany,
  }: {
    crmOpportunity?: Maybe<CrmOpportunityForInput>;
    customFields?: ICustomFieldRecordSchema;
    useGuidedSelling?: boolean;
    selectedBlueprintId?: string;
    hubspotLinkedCompany?: HubspotAssociatedCompany | null;
  } = {}) => {
    try {
      let { contractId, quoteType, ...quoteRequest } = getValues();

      // Create opportunity if user selected to create one from the CRM
      if (crmOpportunity?.id) {
        const opportunity = await doGetOrCreateOpportunity({
          customId: crmOpportunity.id,
          createPayload: {
            accountId: quoteRequest.accountId!,
            name: crmOpportunity.name,
            customId:
              crmOpportunity.id ||
              `Quote for ${accountName || quoteRequest.accountId!}`,
          },
        });
        quoteRequest.opportunityId = opportunity.id;
      }

      // account property is added from accountForm
      // but should not be in the payload since accountId will always be set
      if ('account' in quoteRequest) {
        setAccount(quoteRequest.account as unknown as IAccountRespSchema);
        (quoteRequest as any).account = undefined;
      }

      if (useGuidedSelling) {
        onGuidedSellingOpen();
        return;
      }

      // HubSpot pre-work: create account/contact in MN if they don't exist yet
      const hubspotLinkedAccountResponse =
        await createHubspotLinkedAccountAndContacts({
          hubspotLinkedCompany,
          legalEntityId: quoteRequest.legalEntityId!,
          currency: quoteRequest.currency!,
        });

      // Only HubSpot-sourced linked accounts are resolved here. Any other
      // linked account was already picked by the user in the modal, so leave
      // the form value alone rather than overwriting it with undefined.
      if (hubspotLinkedAccountResponse?.linkedAccountId) {
        quoteRequest.linkedAccountId =
          hubspotLinkedAccountResponse.linkedAccountId;
      }

      if (isTenantEsignConfigured) {
        quoteRequest.requiresEsign = true;
      }

      quoteRequest.customFields = customFields || {};
      const eSignContactFromCrm = parsedFieldMapping?.['contacts.esign'];
      if (!isEmpty(parsedFieldMapping)) {
        parsedFieldMapping?.['contacts.esign'] &&
          delete parsedFieldMapping['contacts.esign'];
        quoteRequest = { ...quoteRequest, ...parsedFieldMapping };
      }

      // If a blueprint is selected, create quote from blueprint
      const createdQuote = selectedBlueprintId
        ? await createQuoteFromBlueprint({
            blueprintId: selectedBlueprintId,
            payload: quoteRequest as IQuoteRequestSchemaExtended,
          })
        : await createQuote(quoteRequest);

      if (createdQuote) {
        await setQuoteContacts(
          createdQuote,
          contactIdSearchParam,
          createdQuote.accountId,
          eSignContactFromCrm,
        );

        queryClient.setQueryData(
          cpqServiceQueryKeys.quotesById(createdQuote.id),
          createdQuote,
        );

        navigate(getQuoteEditRoute(createdQuote.id), { replace: true });
      }
    } catch (error) {
      handleApiErrorToast(error);
      if (hasSkipModalSearchParam) {
        navigate(ROUTES.SALES_LANDING, { replace: true });
      }
    }
  };

  const createHubspotLinkedAccountAndContacts = async ({
    hubspotLinkedCompany,
    currency,
    legalEntityId,
  }: {
    hubspotLinkedCompany?: HubspotAssociatedCompany | null;
    legalEntityId: string;
    currency: string;
  }) => {
    if (!hubspotLinkedCompany) {
      return null;
    }
    let hubspotContactId: Maybe<string>;
    let linkedAccountId = hubspotLinkedCompany.monetizeAccountId;
    let skipContactDuplicateCheck = false;

    if (!linkedAccountId) {
      skipContactDuplicateCheck = true;
      // Create the account in MN from HubSpot company data
      const newAccount = await doCreateAccount({
        accountName: hubspotLinkedCompany.name,
        status: AccountStatusEnum.ACTIVE,
        isTestAccount: false,
        defaultLegalEntityId: legalEntityId,
        defaultCurrency: currency,
        customFields: {},
        ...(hubspotLinkedCompany.billingAddress && {
          billingAddress: {
            line1: hubspotLinkedCompany.billingAddress,
            line2: hubspotLinkedCompany.billingAddressLine2 ?? null,
            city: hubspotLinkedCompany.billingCity ?? null,
            state: hubspotLinkedCompany.billingState ?? null,
            country: hubspotLinkedCompany.billingCountry ?? null,
            postalCode: hubspotLinkedCompany.billingZip ?? null,
          },
        }),
        ...(hubspotLinkedCompany.address && {
          shippingAddress: {
            line1: hubspotLinkedCompany.address,
            line2: hubspotLinkedCompany.addressLine2 ?? null,
            city: hubspotLinkedCompany.city ?? null,
            state: hubspotLinkedCompany.state ?? null,
            country: hubspotLinkedCompany.country ?? null,
            postalCode: hubspotLinkedCompany.zip ?? null,
          },
        }),
      });
      linkedAccountId = newAccount.id;
      // Associate the newly created MN account back to the HubSpot company
      await doAssociateHubspotCompanyWithAccount(
        hubspotLinkedCompany.hubSpotObjectId,
        linkedAccountId,
      ).catch((error) => {
        logger.error(
          'Error associating HubSpot company with MN account',
          error,
        );
        Sentry.captureException(error, { tags: { type: 'QUOTE_CREATION' } });
      });
    }

    // Create all contacts in MN if they don't exist

    const results = await doUpsertHubspotContacts({
      accountId: linkedAccountId,
      associatedContacts: hubspotLinkedCompany.associatedContacts,
      skipContactDuplicateCheck,
    });

    hubspotContactId = results.find((result) => result.contactId)?.contactId;

    // Fire and forget - this is not awaited on so the user does not need to wait for the associations to complete to start working on the quote.
    associateNewContactsWithHubspot(results);

    return { hubspotContactId, linkedAccountId };
  };

  async function associateNewContactsWithHubspot(
    contactCreationResults: IBulkContactResponseSchema['response'],
  ) {
    for (const contact of contactCreationResults) {
      if (contact.contactId && contact.referenceId) {
        await doAssociateHubspotContactWithContact(
          contact.referenceId,
          contact.contactId,
        ).catch((error) => {
          logger.error(
            `Error associating HubSpot contact ${contact.referenceId} with MN contact ${contact.contactId}`,
            error,
          );
          Sentry.captureException(error, { tags: { type: 'QUOTE_CREATION' } });
        });
      }
    }
  }

  /**
   * Create a new account if the user selected that option
   */
  const createNewAccount = useCallback(async (newAccount: IAccountSchema) => {
    const quote = getValues();
    const createdAccount = await doCreateAccount(newAccount);
    quote.accountId = createdAccount.id;
    quote.description = getQuoteDescription(createdAccount?.accountName);
    quote.currency = createdAccount.defaultCurrency;
    setAccount(createdAccount);
    setAccountName(createdAccount.accountName);
    return createdAccount;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * After creating a quote, optionally add a contact to the quote
   * If a contactId is provided, it will be used (from queryParam or the primary billing contact)
   * Else, if the already exists, if there is exactly 1 active contact on the account, use that for the quote
   */
  const setQuoteContacts = useCallback(
    async (
      quote: IQuoteRespSchema,
      contactId: Maybe<string>,
      /** This should be omitted for newly created accounts since they will not have contacts */
      existingAccountId: Maybe<string>,
      /**
       * CRM field mapping may provide an override for the eSign contact id to use here
       */
      eSignContact?: Maybe<string>,
    ) => {
      try {
        let contactIdToSet = contactId;

        if (!contactIdToSet && !eSignContact && existingAccountId) {
          // If no contact provided and there is exactly 1 active contact on the account, use that for the quote
          const activeAccountContacts = (
            await doGetAccountContacts(
              existingAccountId,
              { first: 0, rows: 2, page: 0 },
              { status: ContactStatusEnum.ACTIVE },
            )
          ).content;

          if (activeAccountContacts.length === 1) {
            contactIdToSet = activeAccountContacts[0].id;
          }
        }

        if (eSignContact || contactIdToSet) {
          const esignIds = getDefaultEsignIds({
            quote,
            defaultContactId: contactIdToSet,
            internalEsignContactId: eSignContact,
            isTenantEsignConfigured,
          });

          const quoteContacts = await updateQuoteContacts(quote.id, {
            billingId: contactIdToSet || null,
            primaryId: contactIdToSet || null,
            esignIds,
            esignCcIds: [],
            additionalSignerIds: [],
          });

          if (quoteContacts) {
            quote.contacts = quoteContacts;
          }
        }
      } catch (ex) {
        handleApiErrorToast(new Error('Unable to set quote contacts'));
      }
    },
    [isTenantEsignConfigured, updateQuoteContacts],
  );

  return (
    <FormProvider setValue={setValue} getValues={getValues} {...formMethods}>
      {!isOpen && !isGuidedSellingOpen && <MPageLoader />}
      {isOpen && !isGuidedSellingOpen && (
        <NewQuoteForm
          isOpen
          hasGuidedSellingProcess={!!guidedQuotingProcess}
          fixedAccount={accountById}
          existingOpportunityId={opportunityIdSearchParam}
          legalEntities={legalEntitiesData?.legalEntities || []}
          onClose={handleCancel}
          closeAndContinue={handleCreateQuote}
          onCreateNewAccount={createNewAccount}
          crmCustomFields={parsedFieldMapping.customFields}
          crmCurrency={parsedFieldMapping.currency}
          additionalFields={additionalFields}
        />
      )}
      {!!guidedQuotingProcess && isGuidedSellingOpen && account && (
        <NewGuidedQuoteModal
          account={account}
          existingOpportunityId={opportunityIdSearchParam}
          initialQuoteData={getValues()}
          onCancel={navigateBack}
          getInitialQuoteFieldValues={getGuidedSellingInitialQuoteFieldValues}
          onFinished={async (quote) => {
            try {
              const eSignContactFromCrm =
                parsedFieldMapping?.['contacts.esign'];
              await setQuoteContacts(
                quote,
                contactIdSearchParam,
                quote.accountId,
                eSignContactFromCrm,
              );
            } catch (ex) {
              logger.error('Error setting quote contacts', ex);
              Sentry.captureException(ex, { tags: { type: 'QUOTE_CREATION' } });
            } finally {
              navigate(getQuoteEditRoute(quote.id), { replace: true });
            }
          }}
          onManualQuote={handleCreateQuote}
          parsedFieldMapping={parsedFieldMapping}
        />
      )}
    </FormProvider>
  );
};
